import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * Iter 253s · Ad-hoc peer feedback modal.
 *
 * Renders a 1-5 star picker for the standard rating parameters + comment.
 * Persists via existing `POST /workspaces/{ws}/feedback/internal`.
 * Callers should conditionally show the trigger based on
 * `GET /workspaces/{ws}/feedback/can-create-internal`.
 *
 * Props:
 *   - toUserId: recipient user id (required)
 *   - toUserName: display name
 *   - onClose:
 *   - onSaved:
 */
export default function AdHocFeedbackDialog({ toUserId, toUserName, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [params, setParams] = useState([]);
  const [paramRatings, setParamRatings] = useState({});
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${activeWorkspaceId}/feedback/parameters/standard`).then((r) => {
      setParams(r.data?.parameters || []);
    }).catch(() => setParams([]));
  }, [activeWorkspaceId]);

  const submit = async () => {
    if (!rating) { toast.error("Give an overall rating"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/feedback/internal`, {
        to_user_id: toUserId,
        rating,
        comment: comment || null,
        parameters: paramRatings,
        is_private: isPrivate,
      });
      toast.success(`Feedback sent to ${toUserName || "teammate"}`);
      if (onSaved) onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Feedback failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="adhoc-feedback-dialog">
        <DialogHeader>
          <DialogTitle>Give feedback to {toUserName || "teammate"}</DialogTitle>
          <DialogDescription className="text-xs">
            Rate {toUserName || "your teammate"} on the standard parameters and drop an optional comment. Marking the note private will store it as recipient-only notes without sending a notification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Overall rating *</Label>
            <div className="flex items-center gap-1" data-testid="adhoc-feedback-overall">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button"
                  onClick={() => setRating(n)}
                  className="p-1 hover:scale-110 transition"
                  data-testid={`adhoc-feedback-star-${n}`}>
                  <Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                </button>
              ))}
              {rating > 0 && <span className="text-xs text-muted-foreground ml-2">{rating}/5</span>}
            </div>
          </div>

          {params.length > 0 && (
            <div className="border border-border p-3 space-y-2">
              <Label className="label-eyebrow inline-block">Per-parameter (optional)</Label>
              {params.map((p) => (
                <div key={p} className="flex items-center gap-3">
                  <span className="text-xs flex-1">{p}</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setParamRatings((s) => ({ ...s, [p]: n }))}
                        className="p-0.5"
                        data-testid={`adhoc-feedback-param-${p}-${n}`}
                      >
                        <Star className={`w-3.5 h-3.5 ${n <= (paramRatings[p] || 0) ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <Label className="label-eyebrow mb-1 inline-block">Comment (optional)</Label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder="What did they do well? What could improve?"
                      className="rounded-none" data-testid="adhoc-feedback-comment" />
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)}
                   data-testid="adhoc-feedback-private" />
            Private (don't notify the recipient; only visible to them)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="adhoc-feedback-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !rating} data-testid="adhoc-feedback-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Send feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
